import type { KeyboardEvent } from 'react';
import { useCallback, useRef } from 'react';

export interface HorizontalTab {
  id: string;
  label: string;
  /** When true the tab renders non-interactive (aria-disabled), fires no
   *  onTabChange, and is skipped by arrow-key / Home / End navigation.
   *  Invariant: `activeTab` must never be a disabled tab's id — a disabled
   *  tab is a non-selectable affordance, and selecting one would emit the
   *  contradictory pair aria-selected="true" + aria-disabled="true". */
  disabled?: boolean;
}

interface HorizontalTabsProps {
  tabs: HorizontalTab[];
  /** Must never be a disabled tab's id — see HorizontalTab.disabled. */
  activeTab: string;
  onTabChange: (tabId: string) => void;
  /** Optional ID prefix for ARIA attributes (defaults to 'tab') */
  idPrefix?: string;
  /** Remove the bottom border (useful when a parent provides its own) */
  noBorder?: boolean;
  /**
   * Set when the consumer mounts only the active tab's panel (lazy panels).
   * In that case only the selected tab emits `aria-controls` — pointing at the
   * single rendered panel — because the inactive tabs' panels don't exist in the
   * DOM, and an `aria-controls` referencing a missing id is invalid for screen
   * readers. Defaults to false (every tab controls a rendered panel).
   */
  lazyPanels?: boolean;
  /**
   * Visual treatment. `underline` is the default (border-bottom on the bar,
   * brand underline on the active tab). `pill` renders the active tab as a
   * filled brand pill with no bar border — the record-slideout design's
   * radio-button-like tabs. Same semantics either way (`role=tablist`,
   * arrow-key navigation); only the look changes.
   */
  variant?: 'underline' | 'pill';
  /**
   * By default the bar renders nothing at all with a single tab (a one-tab
   * bar is usually noise). Opt in to keep it visible when the tab acts as
   * the section's heading — e.g. a temporarily-solo tab whose siblings are
   * hidden behind an unreleased feature and will return.
   */
  showSingleTab?: boolean;
}

export default function HorizontalTabs({
  tabs,
  activeTab,
  onTabChange,
  idPrefix = 'tab',
  noBorder = false,
  lazyPanels = false,
  variant = 'underline',
  showSingleTab = false,
}: HorizontalTabsProps) {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const findEnabledIndex = useCallback(
    (start: number, step: 1 | -1): number | null => {
      // Walk up to `tabs.length` steps in `step`'s direction (wrapping) and
      // return the first non-disabled index; null if every tab is disabled.
      // The `+ tabs.length * i` term keeps the operand non-negative before the
      // modulo so the `-1` (ArrowLeft) direction wraps correctly in JS, where
      // `%` returns a negative remainder for negative operands.
      // e.g. tabs.length=3, start=0, step=-1: i=1 -> (0-1+3)%3 = 2 (wraps to last).
      for (let i = 1; i <= tabs.length; i++) {
        const idx = (start + step * i + tabs.length * i) % tabs.length;
        if (!tabs[idx]?.disabled) {
          return idx;
        }
      }
      return null; // all tabs disabled
    },
    [tabs]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
      let newIndex: number | null = null;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          newIndex = findEnabledIndex(currentIndex, -1);
          break;
        case 'ArrowRight':
          event.preventDefault();
          newIndex = findEnabledIndex(currentIndex, 1);
          break;
        case 'Home':
          event.preventDefault();
          newIndex = findEnabledIndex(tabs.length - 1, 1);
          break;
        case 'End':
          event.preventDefault();
          newIndex = findEnabledIndex(0, -1);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (!tabs[currentIndex].disabled) {
            onTabChange(tabs[currentIndex].id);
          }
          return;
        default:
          return;
      }

      if (newIndex !== null && tabRefs.current[newIndex]) {
        tabRefs.current[newIndex]?.focus();
        onTabChange(tabs[newIndex].id);
      }
    },
    [tabs, onTabChange, findEnabledIndex]
  );

  // Hide tab bar when there's only a single tab (unless opted in)
  if (tabs.length <= 1 && !showSingleTab) {
    return null;
  }

  const isPill = variant === 'pill';

  return (
    <div
      className={
        noBorder || isPill
          ? 'overflow-x-auto'
          : 'border-b border-border-secondary overflow-x-auto'
      }
    >
      <div
        role='tablist'
        aria-label='Tabs'
        className={`flex items-center flex-nowrap min-w-max ${
          isPill ? 'gap-1.5' : 'gap-3'
        }`}
      >
        {tabs.map((tab, index) => {
          const isSelected = activeTab === tab.id;
          const isDisabled = tab.disabled ?? false;
          const tabId = `${idPrefix}-${tab.id}`;
          const panelId = `${idPrefix}-panel-${tab.id}`;

          return (
            <button
              key={tab.id}
              ref={el => {
                tabRefs.current[index] = el;
              }}
              id={tabId}
              role='tab'
              aria-selected={isSelected}
              aria-disabled={isDisabled || undefined}
              aria-controls={!lazyPanels || isSelected ? panelId : undefined}
              tabIndex={isSelected ? 0 : -1}
              onClick={() => {
                if (!isDisabled) {
                  onTabChange(tab.id);
                }
              }}
              onKeyDown={e => handleKeyDown(e, index)}
              className={
                isPill
                  ? `rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors cursor-pointer
                     outline-focus-ring focus-visible:outline-2
                     ${
                       isSelected
                         ? // No dark: overrides — utility-brand tokens auto-flip in
                           // dark mode (theme.css remaps 50→950, 700→300), so these
                           // two classes are dark-mode-correct on their own. Explicit
                           // dark: overrides double-flip and land on navy-on-navy.
                           'bg-utility-brand-50 text-utility-brand-700'
                         : 'text-tertiary hover:text-secondary hover:bg-bg-secondary'
                     }
                     ${isDisabled ? 'cursor-not-allowed opacity-50' : ''}`
                  : `
                h-8 px-1 pb-3 text-sm font-semibold transition-colors cursor-pointer
                outline-focus-ring focus-visible:outline-2
                border-b-2
                ${
                  isSelected
                    ? 'border-[var(--color-brand-600)] text-fg-primary'
                    : 'border-transparent text-tertiary hover:text-secondary'
                }
                ${isDisabled ? 'cursor-not-allowed opacity-50' : ''}
              `
              }
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
